import { useAuth } from "@/shared/contexts/auth.context";
import { snakeCase, toUpper, upperCase } from "lodash";
import React, { useEffect } from "react";
import { Navigate, Outlet, useLocation, useSearchParams } from "react-router";
import { useModalContext } from "@/shared/contexts/modal.context";
import { ModalFileName } from "@/shared/modals";

function PrivateRoute() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clearModals, openModal } = useModalContext();
  const { pathname } = location;

  // Clearing modals here on route change because the useLocation causes an
  // infinite re-render of the modal context when used there
  useEffect(() => {
    clearModals();
  }, [pathname]);

  // Opening modals here on route change because the useLocation causes an
  // infinite re-render of the modal context when used there
  useEffect(() => {
    if (searchParams.get("modal") && isAuthenticated) {
      const params = Object.fromEntries(searchParams.entries());
      const modalName = toUpper(snakeCase(searchParams.get("modal") ?? ""));
      openModal(ModalFileName[modalName as keyof typeof ModalFileName], params);
      // Clear the modal and params from the url to avoid it to reopen on page reload
      setSearchParams({});
    }
  }, [searchParams, isAuthenticated]);

  // Redirect to login if the user is not authenticated. In development mode,
  // we don't redirect to login as hot reloads will load the code before the
  // auth context is initialized and the user is constantly logged out.
  if (!isAuthenticated && import.meta.env.MODE !== "development") {
    // Preserve the full URL (pathname + search) for redirect after login
    const redirectUrl = `${location.pathname}${location.search}`;
    localStorage.setItem("authRedirect", redirectUrl);

    return (
      <Navigate
        to={{
          pathname: "/login",
          search: location.search,
        }}
      />
    );
  }

  return <Outlet />;
}

export default PrivateRoute;
